/* eslint-disable react/no-this-in-sfc */
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import { getExcludingDate } from "components/company/v3/brazilian/contents/events/dividends/BrazilianDividendsPaid";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type { SeriesOptionsType } from "highcharts";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface BrazilianDividendsPaidChartProps {
  dividendsPaid: IDividendsPaidDetails[];
}

export default function BrazilianDividendsPaidChart({ dividendsPaid }: BrazilianDividendsPaidChartProps) {
  const intl = useIntl();
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);

  const labels = {
    type: intl.formatMessage({ defaultMessage: "Tipo", id: "wt+XcA" }),
    recordDate: intl.formatMessage({ defaultMessage: "Data-Com", id: "uJFyej" }),
    excludingDate: intl.formatMessage({ defaultMessage: "Data-Ex", id: "tsg1zP" }),
    payDate: intl.formatMessage({ defaultMessage: "Pagamento", id: "23+rmI" }),
    amountPaid: intl.formatMessage({
      defaultMessage: "Valor (R$)",
      id: "XydT1i",
      description: "Valor dos dividendos pagos em reais",
    }),
  };

  const nodes = [
    dividendsPaid.filter((node) => node.dividendType !== "JUROS"),
    dividendsPaid.filter((node) => node.dividendType === "JUROS"),
  ];

  const series: SeriesOptionsType[] = nodes.map((dividends, index) => {
    const theme = getColorThemeForIndex(index);

    if (!theme) {
      throw new Error("Theme not found.");
    }

    const name = index === 0 ? intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" }) : "JCP";

    return {
      type: "column",
      showInNavigator: true,
      animation: false,
      name,
      color: theme.color,
      borderColor: theme.lineColor,
      data: dividends
        .sort((a, b) => compareAsc(parseDateYmd(a.recordDate), parseDateYmd(b.recordDate)))
        .map((node) => {
          const type =
            node.dividendType === "JUROS" ? "JCP" : intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" });

          return {
            x: parseISO(node.recordDate).getTime(),
            y: node.amountPaid,
            name: type,
            custom: node,
          };
        }),
      custom: {
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
    };
  });

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          return intl.formatDate(new Date(this.value), {
            year: "2-digit",
            month: "numeric",
            day: "numeric",
          });
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      type: "linear",
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    series,
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: dividendsPaid.length > 10,
      ...navigatorStyles,
      yAxis: {
        ...navigatorStyles.yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: false,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const point = (this.point as any).custom as IDividendsPaidDetails | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <KeyValueTooltip
            keyValues={[
              {
                key: labels.type,
                value:
                  point.dividendType === "JUROS"
                    ? "JCP"
                    : intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" }),
              },
              {
                key: labels.excludingDate,
                value: getExcludingDate(intl, parseISO(point.recordDate), parseISO(point.excludingDate)),
              },
              {
                key: labels.recordDate,
                value: intl.formatDate(parseISO(point.recordDate), {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                }),
              },
              {
                key: labels.payDate,
                value: intl.formatDate(parseISO(point.payDate), {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                }),
              },
              { key: labels.amountPaid, value: numeral(point.amountPaid).format("0.00").replace(".", ",") },
            ]}
            x={intl.formatDate(parseISO(point.recordDate), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
      },
      column: {
        stacking: "normal",
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />;
}
